<template>
  <div v-if="visible" class="dev_brand_switcher" :class="{ expanded: isOpen }">
    <button class="switcher_toggle" type="button" @click="toggleOpen">
      <span class="toggle_title">DEV</span>
      <span class="toggle_meta">{{ isOpen ? 'Hide' : 'Open' }}</span>
    </button>

    <div v-if="isOpen" class="switcher_panel">
      <div class="switcher_header">
        <div class="switcher_title_group">
          <span class="switcher_title">Debug Panel</span>
          <span class="switcher_desc">Brand and runtime overrides</span>
        </div>
        <button class="collapse_btn" type="button" @click="toggleOpen">×</button>
      </div>

      <div class="switcher_section">
        <div class="section_header">
          <span class="section_title">Brand</span>
          <span class="switcher_status">{{ currentBrand }}</span>
        </div>
        <div class="switcher_actions">
          <button class="switcher_btn" :class="{ active: overrideBrand === null }" type="button" @click="applyBrand(null)">AUTO</button>
          <button v-for="item in brands" :key="item" class="switcher_btn" :class="{ active: currentBrand === item, pinned: overrideBrand === item }" type="button" @click="applyBrand(item)">
            {{ item }}
          </button>
        </div>
      </div>

      <div class="switcher_section runtime_section">
        <div class="section_header">
          <span class="section_title">Electron</span>
          <span class="switcher_status">{{ currentElectronLabel }}</span>
        </div>
        <div class="runtime_meta">
          <span>Detected: {{ detectedElectron ? 'ON' : 'OFF' }}</span>
          <span>Override: {{ electronOverrideLabel }}</span>
        </div>
        <div class="switcher_actions runtime_actions">
          <button class="switcher_btn" :class="{ active: electronOverride === null }" type="button" @click="applyElectron(null)">AUTO</button>
          <button class="switcher_btn" :class="{ active: effectiveElectron }" type="button" @click="applyElectron(true)">FORCE EXE</button>
          <button class="switcher_btn" :class="{ active: electronOverride === false }" type="button" @click="applyElectron(false)">FORCE WEB</button>
        </div>
      </div>

      <div class="switcher_section lag_section">
        <div class="section_header">
          <span class="section_title">Browser Lag</span>
          <span class="switcher_status lag_status" :class="lagStatusClass">{{ lagStatusLabel }}</span>
        </div>
        <div class="runtime_meta">
          <span>Index: {{ lagIndex }}/100</span>
          <span>Last frame: {{ frameDeltaLabel }}</span>
        </div>
        <div class="lag_meter_track">
          <div class="lag_meter_fill" :class="lagStatusClass" :style="{ width: `${lagIndex}%` }"></div>
        </div>
        <div class="lag_metrics">
          <div class="lag_metric_card">
            <span class="lag_metric_label">FPS</span>
            <span class="lag_metric_value">{{ averageFps }}</span>
          </div>
          <div class="lag_metric_card">
            <span class="lag_metric_label">Slow</span>
            <span class="lag_metric_value">{{ slowFrameRate }}%</span>
          </div>
          <div class="lag_metric_card">
            <span class="lag_metric_label">Worst</span>
            <span class="lag_metric_value">{{ worstFrameLabel }}</span>
          </div>
          <div class="lag_metric_card">
            <span class="lag_metric_label">Long task</span>
            <span class="lag_metric_value">{{ longTaskCount }}</span>
          </div>
        </div>
      </div>

      <div class="switcher_section hid_section">
        <div class="section_header">
          <span class="section_title">WebHID</span>
          <span class="switcher_status">{{ hidSupportLabel }}</span>
        </div>
        <div class="switcher_actions hid_actions">
          <button class="switcher_btn" type="button" :disabled="hidRequestPending" @click="requestHidDevice">
            {{ hidRequestPending ? 'REQUESTING...' : 'REQUEST DEVICE' }}
          </button>
        </div>
        <pre class="hid_output">{{ hidRequestResult }}</pre>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Brand, brand, canShowDevBrandSwitcher, getDevBrandOverride, refreshBrand, setDevBrandOverride } from '@/brand/brand'
import { getDetectedElectron, getDevElectronOverride, isElectron, setDevElectronOverride } from '@libs/utils/envUtils'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const DEV_SWITCHER_OPEN_KEY = '__driver_v2_dev_switcher_open__'
const FRAME_BUDGET = 16.7
const FRAME_SAMPLE_LIMIT = 90
const brands = [Brand.iyx, Brand.irok, Brand.carotmas]
const visible = canShowDevBrandSwitcher()
const overrideBrand = ref<Brand | null>(getDevBrandOverride())
const electronOverride = ref<boolean | null>(getDevElectronOverride())
const isOpen = ref(readOpenState())
const currentBrand = computed(() => brand.value)
const effectiveElectron = computed(() => isElectron())
const detectedElectron = computed(() => getDetectedElectron())
const currentElectronLabel = computed(() => (effectiveElectron.value ? 'EXE' : 'WEB'))
const hidRequestPending = ref(false)
const hidRequestResult = ref('No request yet.')
const averageFps = ref(60)
const slowFrameRate = ref(0)
const worstFrameMs = ref(FRAME_BUDGET)
const frameDeltaMs = ref(FRAME_BUDGET)
const lagIndex = ref(0)
const longTaskCount = ref(0)
const longTaskDebtMs = ref(0)
const lagStatusLabel = computed(() => {
  if (lagIndex.value < 20) {
    return 'SMOOTH'
  }

  if (lagIndex.value < 45) {
    return 'STABLE'
  }

  if (lagIndex.value < 70) {
    return 'BUSY'
  }

  return 'JANKY'
})
const lagStatusClass = computed(() => {
  if (lagIndex.value < 20) {
    return 'is_smooth'
  }

  if (lagIndex.value < 45) {
    return 'is_stable'
  }

  if (lagIndex.value < 70) {
    return 'is_busy'
  }

  return 'is_janky'
})
const frameDeltaLabel = computed(() => `${frameDeltaMs.value.toFixed(1)}ms`)
const worstFrameLabel = computed(() => `${worstFrameMs.value.toFixed(0)}ms`)
const hidSupportLabel = computed(() => {
  if (typeof navigator === 'undefined') {
    return 'UNAVAILABLE'
  }

  return 'hid' in navigator ? 'AVAILABLE' : 'UNAVAILABLE'
})
const electronOverrideLabel = computed(() => {
  if (electronOverride.value === null) {
    return 'AUTO'
  }

  return electronOverride.value ? 'FORCE EXE' : 'FORCE WEB'
})

let frameRequestId = 0
let lastFrameAt = 0
let performanceObserver: PerformanceObserver | null = null
const frameSamples: number[] = []

function readOpenState() {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return true
  }

  return localStorage.getItem(DEV_SWITCHER_OPEN_KEY) !== 'false'
}

const toggleOpen = () => {
  isOpen.value = !isOpen.value
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(DEV_SWITCHER_OPEN_KEY, String(isOpen.value))
  }
}

const applyBrand = async (value: Brand | null) => {
  overrideBrand.value = value
  const nextBrand = await setDevBrandOverride(value)
  overrideBrand.value = getDevBrandOverride()
  brand.value = nextBrand
}

const applyElectron = async (value: boolean | null) => {
  electronOverride.value = value
  setDevElectronOverride(value)
  electronOverride.value = getDevElectronOverride()
  brand.value = await refreshBrand()
}

const refreshLagMetrics = (delta: number) => {
  const nextDelta = Math.min(delta, 1000)

  frameDeltaMs.value = nextDelta
  frameSamples.push(nextDelta)
  if (frameSamples.length > FRAME_SAMPLE_LIMIT) {
    frameSamples.shift()
  }

  const totalDelta = frameSamples.reduce((sum, item) => sum + item, 0)
  const averageDelta = totalDelta / frameSamples.length
  const slowFrames = frameSamples.filter((item) => item > 34).length
  const worstFrame = frameSamples.reduce((max, item) => Math.max(max, item), FRAME_BUDGET)
  const droppedFrameDebt = frameSamples.reduce((sum, item) => sum + Math.max(0, item - FRAME_BUDGET), 0)
  const framePenalty = Math.min(80, Math.round(droppedFrameDebt / frameSamples.length))
  const slowPenalty = Math.min(20, Math.round((slowFrames / frameSamples.length) * 40))
  const longTaskPenalty = Math.min(35, Math.round(longTaskDebtMs.value / 24))

  averageFps.value = Math.max(1, Math.round(1000 / averageDelta))
  slowFrameRate.value = Math.round((slowFrames / frameSamples.length) * 100)
  worstFrameMs.value = worstFrame
  lagIndex.value = Math.min(100, framePenalty + slowPenalty + longTaskPenalty)
  longTaskDebtMs.value = Math.max(0, longTaskDebtMs.value - 6)
}

const startLagProbe = () => {
  if (typeof window === 'undefined' || typeof performance === 'undefined') {
    return
  }

  const tick = (timestamp: number) => {
    if (lastFrameAt > 0) {
      refreshLagMetrics(timestamp - lastFrameAt)
    }

    lastFrameAt = timestamp
    frameRequestId = window.requestAnimationFrame(tick)
  }

  frameRequestId = window.requestAnimationFrame(tick)

  if (typeof PerformanceObserver === 'undefined') {
    return
  }

  const supportedEntryTypes = Array.isArray(PerformanceObserver.supportedEntryTypes) ? PerformanceObserver.supportedEntryTypes : []
  if (!supportedEntryTypes.includes('longtask')) {
    return
  }

  performanceObserver = new PerformanceObserver((entryList) => {
    entryList.getEntries().forEach((entry) => {
      longTaskCount.value += 1
      longTaskDebtMs.value = Math.min(400, longTaskDebtMs.value + Math.max(0, entry.duration - 50))
    })
  })
  performanceObserver.observe({ entryTypes: ['longtask'] })
}

const stopLagProbe = () => {
  if (typeof window !== 'undefined' && frameRequestId) {
    window.cancelAnimationFrame(frameRequestId)
  }

  frameRequestId = 0
  lastFrameAt = 0
  performanceObserver?.disconnect()
  performanceObserver = null
}

const requestHidDevice = async () => {
  if (typeof navigator === 'undefined' || !('hid' in navigator)) {
    hidRequestResult.value = 'WebHID is not available in the current runtime.'
    return
  }

  hidRequestPending.value = true

  try {
    const devices = await navigator.hid.requestDevice({ filters: [] })
    const result = devices.map((device) => ({
      productName: device.productName,
      vendorId: device.vendorId,
      productId: device.productId,
      opened: device.opened,
      collections: device.collections.length,
    }))

    console.log('navigator.hid.requestDevice result', devices)
    hidRequestResult.value = JSON.stringify(result, null, 2)
  } catch (error) {
    console.error('navigator.hid.requestDevice failed', error)
    if (error instanceof Error) {
      hidRequestResult.value = `${error.name}: ${error.message}`
    } else {
      hidRequestResult.value = String(error)
    }
  } finally {
    hidRequestPending.value = false
  }
}

onMounted(() => {
  startLagProbe()
})

onBeforeUnmount(() => {
  stopLagProbe()
})
</script>

<style scoped lang="less">
.dev_brand_switcher {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 6160;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.switcher_toggle {
  min-width: 84px;
  min-height: 40px;
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: none;
  border-radius: var(--radius-m);
  background: rgba(255, 255, 255, 0.84);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(var(--color-brand-1-rgb), 0.18);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);
  color: var(--color-dark);
  cursor: pointer;
  transition: var(--transition-base);
}

.switcher_toggle:hover {
  transform: translateY(-2px);
  background: rgba(255, 255, 255, 0.94);
}

.toggle_title {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.12em;
}

.toggle_meta {
  color: var(--brand-color-pure);
  font-size: 11px;
  font-weight: 700;
}

.switcher_panel {
  width: 268px;
  margin-top: 10px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  border-radius: var(--radius-m);
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(var(--color-brand-1-rgb), 0.18);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);
}

.switcher_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.switcher_title_group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.switcher_title {
  color: var(--color-dark);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.switcher_desc {
  color: var(--color-text-hint);
  font-size: 11px;
  font-weight: 600;
}

.switcher_status {
  color: var(--brand-color-pure);
  font-size: 12px;
  font-weight: 700;
}

.collapse_btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.04);
  color: var(--sidebar-text-muted);
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  transition: var(--transition-base);
}

.collapse_btn:hover {
  background: rgba(255, 255, 255, 0.9);
  color: var(--color-dark);
}

.switcher_section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.section_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.section_title {
  color: var(--color-dark);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.runtime_meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: var(--color-text-hint);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.switcher_actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.runtime_actions {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.lag_section {
  gap: 12px;
}

.lag_status.is_smooth {
  color: #16a34a;
}

.lag_status.is_stable {
  color: var(--brand-color-pure);
}

.lag_status.is_busy {
  color: #f59e0b;
}

.lag_status.is_janky {
  color: #ef4444;
}

.lag_meter_track {
  width: 100%;
  height: 8px;
  overflow: hidden;
  border-radius: var(--radius-pill);
  background: rgba(0, 0, 0, 0.06);
}

.lag_meter_fill {
  height: 100%;
  border-radius: var(--radius-pill);
  background: var(--brand-color);
  transition:
    width 0.2s ease,
    background 0.2s ease;
}

.lag_meter_fill.is_smooth {
  background: linear-gradient(90deg, #22c55e 0%, #4ade80 100%);
}

.lag_meter_fill.is_stable {
  background: var(--brand-color);
}

.lag_meter_fill.is_busy {
  background: linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%);
}

.lag_meter_fill.is_janky {
  background: linear-gradient(90deg, #ef4444 0%, #fb7185 100%);
}

.lag_metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.lag_metric_card {
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-radius: var(--radius-s);
  background: rgba(255, 255, 255, 0.68);
}

.lag_metric_label {
  color: var(--color-text-hint);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.lag_metric_value {
  color: var(--color-dark);
  font-size: 16px;
  font-weight: 800;
}

.hid_actions {
  grid-template-columns: 1fr;
}

.switcher_btn {
  min-height: 36px;
  border: none;
  border-radius: var(--radius-m);
  background: rgba(255, 255, 255, 0.82);
  color: var(--color-dark);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: var(--transition-base);
}

.switcher_btn:hover {
  transform: translateY(-2px);
  background: rgba(var(--color-brand-1-rgb), 0.12);
}

.switcher_btn.active {
  background: var(--brand-color);
  color: var(--color-white);
  box-shadow: 0 8px 18px rgba(var(--color-brand-1-rgb), 0.22);
}

.switcher_btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  transform: none;
}

.switcher_btn.pinned {
  outline: 2px solid rgba(var(--color-brand-1-rgb), 0.22);
  outline-offset: 1px;
}

.hid_output {
  margin: 0;
  padding: 10px 12px;
  min-height: 84px;
  max-height: 220px;
  overflow: auto;
  border-radius: var(--radius-s);
  background: rgba(255, 255, 255, 0.68);
  color: var(--color-dark);
  font-size: 11px;
  font-weight: 600;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}
@media (max-width: 900px) {
  .dev_brand_switcher {
    right: 16px;
    bottom: 16px;
  }

  .switcher_panel {
    width: 236px;
  }

  .runtime_actions {
    grid-template-columns: 1fr;
  }
}
</style>
